import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewContainerRef,
} from "@angular/core";
import { DialogService } from "@bca-core/modules/dialog/services/dialog.service";
import { AvoTrackingService } from "@bca-core/services/avo-tracking.service";
import { RouterService } from "@bca-core/services/router.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import { ConfirmDialog } from "@bca-shared/components/confirm-dialog/confirm-dialog";
import { DialogIdentifier, Route } from "@bca-shared/models/_enums";
import { DialogId } from "@bca-shared/models/_types";
import { IAddon } from "@bca-shared/models/addon";
import { ICompany } from "@bca-shared/models/company";
import { APP_CONFIG, IAppConfiguration } from "@bca-shared/models/config";
import { IProduct } from "@bca-shared/models/product";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import {
  selectAllProducts,
  selectTotalProductCount,
} from "@bca-store/store.selectors";
import * as userDataActions from "@bca-user-data/store/user-data.actions";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { combineLatest, map, withLatestFrom } from "rxjs/operators";

@Component({
  selector: "bca-product-summary",
  templateUrl: "product-summary.component.html",
  styleUrls: ["product-summary.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductSummaryComponent {
  products$: Observable<IProduct[]>;
  totalProductCount$: Observable<number>;
  totalReceiptPrice$: Observable<number>;
  subTotalReceiptPrice$: Observable<number>;
  tax$: Observable<number>;
  company$: Observable<ICompany>;

  constructor(
    private _store$: Store<IRootState>,
    private _routerService: RouterService,
    private _translate: TranslateService,
    private _dialogService: DialogService,
    private _viewContainerRef: ViewContainerRef,
    private _avoTrackingService: AvoTrackingService,
    @Inject(APP_CONFIG) public config: IAppConfiguration,
  ) {
    this.products$ = this._store$.pipe(select(selectAllProducts));
    this.totalProductCount$ = this._store$.pipe(
      select(selectTotalProductCount),
    );
    this.subTotalReceiptPrice$ = this._store$.pipe(
      select(({ receipt }) => receipt.totalPrice),
    );
    this.tax$ = this.subTotalReceiptPrice$.pipe(
      map((subTotalReceiptPrice) => {
        const { taxRate } = this.config;
        return Math.round(subTotalReceiptPrice * taxRate);
      }),
    );
    this.totalReceiptPrice$ = this.subTotalReceiptPrice$.pipe(
      combineLatest(this.tax$),
      map(([subTotalReceiptPrice, tax]) => subTotalReceiptPrice + tax),
    );
    this.company$ = this._store$.pipe(select(({ receipt }) => receipt.company));
  }

  onProductDelete(product: IProduct): void {
    this._store$.dispatch(
      this._createAction(
        new appActions.SetDialogStatus({
          visible: true,
          identifier: DialogIdentifier.Confirm,
        }),
      ),
    );

    const dialog = this._dialogService.open(
      ConfirmDialog,
      DialogIdentifier.Confirm,
      this._viewContainerRef,
      { disableClose: true },
      {
        title: this._translate.instant("DIALOG.PRODUCT.CONFIRM_DELETE_TITLE"),
        body: this._translate.instant("DIALOG.PRODUCT.CONFIRM_DELETE_BODY", {
          subject: this._translate.instant(
            `COMPOSE.PRODUCTS_OVERVIEW.PRODUCTS.${product.identifier}.TITLE`,
          ),
        }),
        product: product,
      },
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    dialog.instance
      .accept()
      .pipe(
        withLatestFrom(
          this._store$.pipe(select(({ userData }) => userData.selectedProduct)),
        ),
        map(([, selectedProduct]) => selectedProduct),
      )
      .subscribe((selectedProduct) => {
        if (selectedProduct && selectedProduct.id === product.id) {
          this._store$.dispatch(
            this._createAction(new userDataActions.SelectProduct(null)),
          );
        }
        this._store$.dispatch(
          this._createAction(new receiptActions.DeleteProduct(product)),
        );
      });
  }

  onAddonDelete(addon: IAddon, productId: number): void {
    this._store$.dispatch(
      this._createAction(
        new appActions.SetDialogStatus({
          visible: true,
          identifier: DialogIdentifier.Confirm,
        }),
      ),
    );

    const dialog = this._dialogService.open(
      ConfirmDialog,
      DialogIdentifier.Confirm,
      this._viewContainerRef,
      { disableClose: true },
      {
        title: this._translate.instant("DIALOG.ADDON.CONFIRM_DELETE_TITLE"),
        body: this._translate.instant("DIALOG.ADDON.CONFIRM_DELETE_BODY", {
          subject: this._translate.instant(
            `COMPOSE.CHOOSE_EXTRAS.ADDON.${addon.identifier}.TITLE`,
          ),
        }),
      },
      (visible: boolean, identifier: DialogId) => {
        this._store$.dispatch(
          this._createAction(
            new appActions.SetDialogStatus({ visible, identifier }),
          ),
        );
      },
    );

    dialog.instance.accept().subscribe(() => {
      this._store$.dispatch(
        this._createAction(
          new receiptActions.DeleteAddon({ addon, productId }),
        ),
      );
    });
  }

  onProductEdit(product: IProduct): void {
    this._avoTrackingService.trackOrderChange(product);
    this._store$.dispatch(
      this._createAction(new userDataActions.SelectProduct(product)),
    );
    this._routerService.navigateTo(Route.COMPOSE);
  }

  onUserEdit(product: IProduct): void {
    this._store$.dispatch(
      this._createAction(new userDataActions.SelectProduct(product)),
    );
    this._routerService.navigateTo(Route.USER_DATA);
  }

  private _createAction(action: IAction): IAction {
    return createAction(action, ProductSummaryComponent);
  }
}
